import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, FileText, Globe, Calculator, CheckCircle, AlertCircle, 
  ChevronRight, Package, Truck, ClipboardList, Info, ExternalLink,
  Building2, ShieldCheck, Clock, DollarSign, ArrowRight, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function ExportTools() {
  const [activeTab, setActiveTab] = useState('hs-code');
  
  // HS Code Search State
  const [hsQuery, setHsQuery] = useState('');
  const [hsResults, setHsResults] = useState([]);
  const [searchingHS, setSearchingHS] = useState(false);
  
  // Compliance Checker State
  const [complianceHS, setComplianceHS] = useState('');
  const [complianceCountry, setComplianceCountry] = useState('');
  const [complianceResult, setComplianceResult] = useState(null);
  const [checkingCompliance, setCheckingCompliance] = useState(false);
  
  // Duty Calculator State
  const [dutyHS, setDutyHS] = useState('');
  const [dutyCountry, setDutyCountry] = useState('');
  const [dutyValue, setDutyValue] = useState('');
  const [dutyResult, setDutyResult] = useState(null);
  const [calculatingDuty, setCalculatingDuty] = useState(false);
  
  // Countries list
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Fetch countries on mount
  const fetchCountries = useCallback(async () => {
    if (countries.length > 0) return;
    setLoadingCountries(true);
    try {
      const response = await axios.get(`${API}/api/export-tools/countries`);
      setCountries(response.data.countries || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoadingCountries(false);
    }
  }, [countries.length]);

  // Search HS Codes
  const searchHSCodes = async () => {
    if (!hsQuery.trim()) {
      toast.error('Please enter a product name or HS code');
      return;
    }
    
    setSearchingHS(true);
    try {
      const response = await axios.get(`${API}/api/export-tools/hs-codes/search`, {
        params: { query: hsQuery, limit: 15 }
      });
      setHsResults(response.data.results || []);
      if (response.data.results?.length === 0) {
        toast.info('No matching HS codes found. Try different keywords.');
      }
    } catch (error) {
      toast.error('Failed to search HS codes');
    } finally {
      setSearchingHS(false);
    }
  };

  // Check Compliance
  const checkCompliance = async () => {
    if (!complianceHS || !complianceCountry) {
      toast.error('Please enter HS code and select destination country');
      return;
    }
    
    setCheckingCompliance(true);
    try {
      const response = await axios.post(`${API}/api/export-tools/compliance-check`, {
        hs_code: complianceHS,
        destination_country: complianceCountry
      });
      setComplianceResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check compliance');
    } finally {
      setCheckingCompliance(false);
    }
  };

  // Calculate Duties
  const calculateDuties = async () => {
    if (!dutyHS || !dutyCountry || !dutyValue) {
      toast.error('Please fill all fields');
      return;
    }
    
    setCalculatingDuty(true);
    try {
      const response = await axios.post(`${API}/api/export-tools/duty-calculator`, {
        hs_code: dutyHS,
        destination_country: dutyCountry,
        product_value: parseFloat(dutyValue),
        currency: 'USD'
      });
      setDutyResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate duties');
    } finally {
      setCalculatingDuty(false);
    }
  };

  // Apply HS code to other tools
  const applyHSCode = (code) => {
    setComplianceHS(code);
    setDutyHS(code);
    toast.success(`HS Code ${code} applied to other tools`);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="container mx-auto px-4 py-6 pb-24 md:pb-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
                <Globe className="h-6 w-6 text-white" />
              </div>
              Export Compliance Tools
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-2">
              HS Code lookup, compliance requirements, and duty calculator for your exports
            </p>
          </div>

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); fetchCountries(); }} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 lg:w-[500px]">
              <TabsTrigger value="hs-code" className="flex items-center gap-2">
                <Search className="h-4 w-4" />
                <span className="hidden sm:inline">HS Code</span>
              </TabsTrigger>
              <TabsTrigger value="compliance" className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Compliance</span>
              </TabsTrigger>
              <TabsTrigger value="duties" className="flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                <span className="hidden sm:inline">Duties</span>
              </TabsTrigger>
            </TabsList>

            {/* HS Code Search Tab */}
            <TabsContent value="hs-code" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-emerald-600" />
                    HS Code Finder
                  </CardTitle>
                  <CardDescription>
                    Search by product name to find the correct Harmonized System code for customs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter product name (e.g., rice, cotton, spices, jewelry)"
                      value={hsQuery}
                      onChange={(e) => setHsQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchHSCodes()}
                      className="flex-1"
                      data-testid="hs-code-search-input"
                    />
                    <Button onClick={searchHSCodes} disabled={searchingHS} data-testid="hs-code-search-btn">
                      {searchingHS ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      <span className="ml-2 hidden sm:inline">Search</span>
                    </Button>
                  </div>

                  {/* Results */}
                  {hsResults.length > 0 && (
                    <div className="space-y-3 mt-4">
                      <p className="text-sm text-slate-600">Found {hsResults.length} matching codes:</p>
                      {hsResults.map((result, index) => (
                        <div 
                          key={index}
                          className="p-4 border rounded-lg hover:border-emerald-300 hover:bg-emerald-50/50 transition-all"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="font-mono text-emerald-700 border-emerald-300">
                                  {result.code}
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {result.match_type}
                                </Badge>
                              </div>
                              <p className="font-medium text-slate-800">{result.description}</p>
                              <p className="text-sm text-slate-500 mt-1">{result.chapter}</p>
                              
                              {result.examples && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {result.examples.map((ex, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs">
                                      {ex}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              
                              {result.common_destinations && (
                                <p className="text-xs text-slate-500 mt-2">
                                  <Globe className="h-3 w-3 inline mr-1" />
                                  Common markets: {result.common_destinations.join(', ')}
                                </p>
                              )}
                            </div>
                            
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => applyHSCode(result.code)}
                              className="shrink-0"
                            >
                              Use Code
                              <ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex gap-3">
                      <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">About HS Codes</p>
                        <p>The Harmonized System (HS) is an international standardized system of names and numbers to classify traded products. The first 6 digits are internationally standardized; additional digits are country-specific.</p>
                        <a 
                          href="https://www.dgft.gov.in/CP/?opt=hs-code" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-blue-600 hover:underline"
                        >
                          View official DGFT HS Code database
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Compliance Checker Tab */}
            <TabsContent value="compliance" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-emerald-600" />
                    Export Compliance Checker
                  </CardTitle>
                  <CardDescription>
                    Get required documents, certifications, and regulatory requirements by destination
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>HS Code</Label>
                      <Input
                        placeholder="e.g., 0910 or 5208"
                        value={complianceHS}
                        onChange={(e) => setComplianceHS(e.target.value)}
                        data-testid="compliance-hs-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Destination Country</Label>
                      <Select value={complianceCountry} onValueChange={setComplianceCountry}>
                        <SelectTrigger data-testid="compliance-country-select">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {loadingCountries ? (
                            <SelectItem value="loading" disabled>Loading...</SelectItem>
                          ) : (
                            countries.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button 
                    onClick={checkCompliance} 
                    disabled={checkingCompliance}
                    className="w-full md:w-auto"
                    data-testid="check-compliance-btn"
                  >
                    {checkingCompliance ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <ShieldCheck className="h-4 w-4 mr-2" />
                    )}
                    Check Requirements
                  </Button>

                  {/* Compliance Results */}
                  {complianceResult && (
                    <div className="mt-6 space-y-4">
                      {/* Header */}
                      <div className="p-4 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-emerald-100 text-sm">Export Compliance Report</p>
                            <h3 className="text-xl font-bold">
                              {complianceResult.hs_code} → {complianceResult.destination_country}
                            </h3>
                          </div>
                          <Badge className="bg-white/20 text-white border-0">
                            {complianceResult.product_category}
                          </Badge>
                        </div>
                        {complianceResult.trade_agreement && complianceResult.trade_agreement !== "None" && (
                          <p className="mt-2 text-emerald-100 text-sm flex items-center gap-1">
                            <CheckCircle className="h-4 w-4" />
                            Trade Agreement: {complianceResult.trade_agreement}
                          </p>
                        )}
                      </div>

                      {/* Required Documents */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <FileText className="h-4 w-4 text-blue-600" />
                            Required Documents
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {complianceResult.required_documents?.map((doc, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm">
                                <CheckCircle className="h-4 w-4 text-emerald-500" />
                                {doc}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>

                      {/* Category Requirements */}
                      {complianceResult.category_requirements && (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-purple-600" />
                              {complianceResult.product_category.charAt(0).toUpperCase() + complianceResult.product_category.slice(1)} Requirements
                              {complianceResult.category_requirements.agency && (
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {complianceResult.category_requirements.agency}
                                </Badge>
                              )}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <ul className="space-y-2">
                              {complianceResult.category_requirements.requirements?.map((req, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                                  {req}
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      )}

                      {/* Certifications */}
                      {complianceResult.required_certifications?.length > 0 && (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <ShieldCheck className="h-4 w-4 text-emerald-600" />
                              Required Certifications
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex flex-wrap gap-2">
                              {complianceResult.required_certifications.map((cert, i) => (
                                <Badge key={i} variant="secondary" className="text-sm">
                                  {cert}
                                </Badge>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Tips */}
                      {complianceResult.tips?.length > 0 && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                            <Info className="h-4 w-4" />
                            Important Tips
                          </p>
                          <ul className="space-y-1 text-sm text-amber-700">
                            {complianceResult.tips.map((tip, i) => (
                              <li key={i}>• {tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Footer Info */}
                      <div className="flex items-center justify-between text-sm text-slate-500 pt-2">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Avg. clearance: {complianceResult.avg_clearance_days}
                        </span>
                        <span>{complianceResult.import_duty_info}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Duty Calculator Tab */}
            <TabsContent value="duties" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-emerald-600" />
                    Import Duty Calculator
                  </CardTitle>
                  <CardDescription>
                    Estimate import duties and potential savings from trade agreements
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>HS Code</Label>
                      <Input
                        placeholder="e.g., 0910"
                        value={dutyHS}
                        onChange={(e) => setDutyHS(e.target.value)}
                        data-testid="duty-hs-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Destination Country</Label>
                      <Select value={dutyCountry} onValueChange={setDutyCountry}>
                        <SelectTrigger data-testid="duty-country-select">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Product Value (USD)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 10000"
                        value={dutyValue}
                        onChange={(e) => setDutyValue(e.target.value)}
                        data-testid="duty-value-input"
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={calculateDuties} 
                    disabled={calculatingDuty}
                    className="w-full md:w-auto"
                    data-testid="calculate-duty-btn"
                  >
                    {calculatingDuty ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Calculator className="h-4 w-4 mr-2" />
                    )}
                    Calculate Duties
                  </Button>

                  {/* Duty Results */}
                  {dutyResult && (
                    <div className="mt-6 space-y-4">
                      {/* Summary Cards */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Standard Duty */}
                        <Card className="border-slate-200">
                          <CardContent className="pt-6">
                            <div className="text-center">
                              <p className="text-sm text-slate-500 mb-1">Standard Duty Rate</p>
                              <p className="text-3xl font-bold text-slate-700">
                                {dutyResult.standard_duty.rate}
                              </p>
                              <p className="text-lg text-slate-600 mt-2">
                                ${dutyResult.standard_duty.amount.toLocaleString()}
                              </p>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Preferential Duty */}
                        <Card className={`border-2 ${dutyResult.preferential_duty.savings > 0 ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`}>
                          <CardContent className="pt-6">
                            <div className="text-center">
                              <p className="text-sm text-slate-500 mb-1">
                                {dutyResult.preferential_duty.trade_agreement !== "Not applicable" 
                                  ? "With Trade Agreement" 
                                  : "Preferential Rate"}
                              </p>
                              <p className={`text-3xl font-bold ${dutyResult.preferential_duty.savings > 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
                                {dutyResult.preferential_duty.rate}
                              </p>
                              <p className="text-lg text-slate-600 mt-2">
                                ${dutyResult.preferential_duty.amount.toLocaleString()}
                              </p>
                              {dutyResult.preferential_duty.savings > 0 && (
                                <Badge className="mt-2 bg-emerald-500">
                                  Save ${dutyResult.preferential_duty.savings.toLocaleString()}
                                </Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Trade Agreement Info */}
                      {dutyResult.preferential_duty.trade_agreement !== "Not applicable" && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-2 text-blue-800">
                            <CheckCircle className="h-5 w-5" />
                            <span className="font-medium">
                              Trade Agreement: {dutyResult.preferential_duty.trade_agreement}
                            </span>
                          </div>
                          <p className="text-sm text-blue-600 mt-1">
                            Ensure you have proper Certificate of Origin (COO) to claim preferential rates
                          </p>
                        </div>
                      )}

                      {/* Additional Charges */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base">Additional Charges to Consider</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-1 text-sm text-slate-600">
                            {dutyResult.additional_charges.items.map((item, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <DollarSign className="h-3 w-3 text-slate-400" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>

                      {/* Disclaimer */}
                      <p className="text-xs text-slate-500 italic">
                        {dutyResult.disclaimer}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Quick Links */}
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <a 
              href="https://www.dgft.gov.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-4 border rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-all flex items-center gap-3"
            >
              <Globe className="h-8 w-8 text-emerald-600" />
              <div>
                <p className="font-medium text-slate-800">DGFT Portal</p>
                <p className="text-sm text-slate-500">IEC, Licenses, Policies</p>
              </div>
              <ExternalLink className="h-4 w-4 text-slate-400 ml-auto" />
            </a>
            
            <a 
              href="https://www.icegate.gov.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-4 border rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-all flex items-center gap-3"
            >
              <Truck className="h-8 w-8 text-blue-600" />
              <div>
                <p className="font-medium text-slate-800">ICEGATE</p>
                <p className="text-sm text-slate-500">Customs EDI System</p>
              </div>
              <ExternalLink className="h-4 w-4 text-slate-400 ml-auto" />
            </a>
            
            <a 
              href="https://www.cbic.gov.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-4 border rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-all flex items-center gap-3"
            >
              <ClipboardList className="h-8 w-8 text-purple-600" />
              <div>
                <p className="font-medium text-slate-800">CBIC</p>
                <p className="text-sm text-slate-500">Tariffs & Notifications</p>
              </div>
              <ExternalLink className="h-4 w-4 text-slate-400 ml-auto" />
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
}
